import { useState } from 'react';

interface User {
  id: number;
  nickName: string;
  email: string;
  password_hash?: string;
  google_id?: string;
  name?: string;
  resetPasswordToken?: string;
  resetPasswordExpires?: Date;
  verified?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
  role?: string;
  status?: 'Active' | 'Inactive';
  walletAddress?: string;
}

interface ViewUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
}

const ViewUserModal: React.FC<ViewUserModalProps> = ({ isOpen, onClose, user }) => {
  const [activeTab, setActiveTab] = useState('profile');


  const [showProfile, setShowProfile] = useState(true);
  const [showAssets, setShowAssets] = useState(false);
  const [showItems, setShowItems] = useState(false);
  const [showCoins, setShowCoins] = useState(false);
  const [showWallet, setShowWallet] = useState(false);

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="gothic-panel w-full max-w-3xl flex flex-col md:flex-row">
        <div className="w-full md:w-1/4 flex flex-row flex-wrap justify-center md:flex-col md:space-y-2 md:space-x-0 mb-4 md:mb-0 md:pr-4">
          <button onClick={() => setActiveTab('profile')} className={`px-4 py-2 rounded-md font-bold text-gray-300 transition duration-300 ease-in-out transform hover:scale-105 border border-gray-700 bg-gray-800 bg-opacity-50 ${activeTab === 'profile' ? 'border-gold-500 text-gold-300' : ''}`}>Profile</button>
          <button onClick={() => setActiveTab('assets')} className={`px-4 py-2 rounded-md font-bold text-gray-300 transition duration-300 ease-in-out transform hover:scale-105 border border-gray-700 bg-gray-800 bg-opacity-50 ${activeTab === 'assets' ? 'border-gold-500 text-gold-300' : ''}`}>Assets</button>
          <button onClick={() => setActiveTab('items')} className={`px-4 py-2 rounded-md font-bold text-gray-300 transition duration-300 ease-in-out transform hover:scale-105 border border-gray-700 bg-gray-800 bg-opacity-50 ${activeTab === 'items' ? 'border-gold-500 text-gold-300' : ''}`}>Items</button>
          <button onClick={() => setActiveTab('coins')} className={`px-4 py-2 rounded-md font-bold text-gray-300 transition duration-300 ease-in-out transform hover:scale-105 border border-gray-700 bg-gray-800 bg-opacity-50 ${activeTab === 'coins' ? 'border-gold-500 text-gold-300' : ''}`}>Coins</button>
          <button onClick={() => setActiveTab('wallet')} className={`px-4 py-2 rounded-md font-bold text-gray-300 transition duration-300 ease-in-out transform hover:scale-105 border border-gray-700 bg-gray-800 bg-opacity-50 ${activeTab === 'wallet' ? 'border-gold-500 text-gold-300' : ''}`}>Wallet</button>
        </div>

        <div className="flex-grow">
          <h2 className="gothic-heading text-2xl mb-4">User Details</h2>

          {activeTab === 'profile' && (
            <div className="mb-4">
              <p className="gothic-text-silver text-sm mb-1">ID: <span className="gothic-text-gold">{user.id}</span></p>
              <p className="gothic-text-silver text-sm mb-1">Username: <span className="gothic-text-gold">{user.nickName || user.name || 'N/A'}</span></p>
              <p className="gothic-text-silver text-sm mb-1">Email: <span className="gothic-text-gold">{user.email}</span></p>
              {user.password_hash && <p className="gothic-text-silver text-sm mb-1">Password Hash: <span className="gothic-text-gold">{user.password_hash}</span></p>}
              {user.google_id && <p className="gothic-text-silver text-sm mb-1">Google ID: <span className="gothic-text-gold">{user.google_id}</span></p>}
              {user.name && <p className="gothic-text-silver text-sm mb-1">Name: <span className="gothic-text-gold">{user.name}</span></p>}
              {user.resetPasswordToken && <p className="gothic-text-silver text-sm mb-1">Reset Token: <span className="gothic-text-gold">{user.resetPasswordToken}</span></p>}
              {user.resetPasswordExpires && <p className="gothic-text-silver text-sm mb-1">Reset Expires: <span className="gothic-text-gold">{new Date(user.resetPasswordExpires).toLocaleString()}</span></p>}
              {user.verified !== undefined && <p className="gothic-text-silver text-sm mb-1">Verified: <span className="gothic-text-gold">{user.verified ? 'Yes' : 'No'}</span></p>}
              {user.createdAt && <p className="gothic-text-silver text-sm mb-1">Created At: <span className="gothic-text-gold">{new Date(user.createdAt).toLocaleString()}</span></p>}
              {user.updatedAt && <p className="gothic-text-silver text-sm mb-1">Updated At: <span className="gothic-text-gold">{new Date(user.updatedAt).toLocaleString()}</span></p>}
              {user.role && <p className="gothic-text-silver text-sm mb-1">Role: <span className="gothic-text-gold">{user.role}</span></p>}
              {user.status && <p className="gothic-text-silver text-sm mb-1">Status: <span className="gothic-text-gold">{user.status}</span></p>}
              {user.walletAddress && <p className="gothic-text-silver text-sm mb-1">Wallet Address: <span className="gothic-text-gold">{user.walletAddress}</span></p>}
            </div>
          )}

          {activeTab === 'assets' && (
            <div className="mb-4">
              <h3 className="gothic-heading text-xl mb-2">User Assets</h3>
              <p className="gothic-text-silver">Placeholder for user assets data.</p>
            </div>
          )}

          {activeTab === 'items' && (
            <div className="mb-4">
              <h3 className="gothic-heading text-xl mb-2">User Items</h3>
              <p className="gothic-text-silver">Placeholder for user items data.</p>
            </div>
          )}

          {activeTab === 'coins' && (
            <div className="mb-4">
              <h3 className="gothic-heading text-xl mb-2">User Coins</h3>
              <p className="gothic-text-silver">Placeholder for user coins data.</p>
            </div>
          )}

          {activeTab === 'wallet' && (
            <div className="mb-4">
              <h3 className="gothic-heading text-xl mb-2">User Wallet</h3>
              <p className="gothic-text-silver">Placeholder for user wallet data.</p>
            </div>
          )}

          <div className="flex justify-end mt-4">
            <button
              onClick={onClose}
              className="gothic-button gothic-button-primary"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewUserModal;